import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { lookupCountry } from '../../lib/graphApi';
import type { CountryLookupResult } from '../../lib/graphApi';
import { actorDetailPath, familyDetailPath, techniqueDetailPath } from '../../lib/paths';
import { countryFlag, countryLabel } from '../../lib/countryFlag';

const PAGE_SIZE = 30;

export default function CountryDetailPage() {
  const params = useParams();
  const navigate = useNavigate();

  const code = (params.code || '').toUpperCase();

  const [docsExpanded, setDocsExpanded] = useState(false);

  const { data, isLoading } = useQuery<CountryLookupResult>({
    queryKey: ['country-detail', code],
    queryFn: () => lookupCountry(code),
    enabled: !!code,
  });

  const handleCopy = () => {
    void navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const flag = countryFlag(code);
  const displayName = data?.name || countryLabel(code) || code;
  const actors = data?.actors || [];
  const families = data?.top_families || [];
  const techniques = data?.top_techniques || [];
  const documents = data?.documents || [];
  const visibleDocs = docsExpanded ? documents : documents.slice(0, PAGE_SIZE);

  const filteredExploreUrl = `/explore?cat=actor&country=${encodeURIComponent(code)}`;

  if (isLoading) {
    return (
      <div className="ioc-detail-page">
        <button className="ioc-detail-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="ioc-detail-loading">Loading country</div>
      </div>
    );
  }

  return (
    <div className="ioc-detail-page actor-detail-page">
      <button className="ioc-detail-back" onClick={() => navigate(-1)}>&larr; Back</button>

      {/* ── Header ── */}
      <div className="ioc-detail-header">
        <div className="ioc-detail-header-top">
          <span className="ioc-detail-type-badge actor-detail-badge">Country</span>
          <div className="ioc-detail-actions">
            <button className="ioc-detail-action-btn" onClick={handleCopy} title="Copy ISO-2 code">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              Copy
            </button>
            <Link to={filteredExploreUrl} className="ioc-detail-action-btn">
              Browse actors
            </Link>
          </div>
        </div>
        <h1 className="ioc-detail-value actor-detail-name">
          {flag && <span aria-hidden="true" style={{ marginRight: 12, fontSize: '1em' }}>{flag}</span>}
          {displayName}
          <span style={{ marginLeft: 12, fontSize: '0.6em', opacity: 0.6, fontWeight: 400 }}>{code}</span>
        </h1>

        {!!data && (data.actor_count > 0 || data.family_count > 0 || data.doc_count > 0) && (
          <div className="ioc-detail-stats-strip">
            {data.actor_count > 0 && <span>{data.actor_count} actor{data.actor_count !== 1 ? 's' : ''}</span>}
            {data.family_count > 0 && <span>{data.family_count} famil{data.family_count !== 1 ? 'ies' : 'y'}</span>}
            {data.technique_count > 0 && <span>{data.technique_count} technique{data.technique_count !== 1 ? 's' : ''}</span>}
            {data.doc_count > 0 && <span>{data.doc_count} document{data.doc_count !== 1 ? 's' : ''}</span>}
          </div>
        )}
      </div>

      {data && !data.found && (
        <div className="ioc-detail-not-found">
          No actors attributed to this country in the knowledge graph.
        </div>
      )}

      {/* ── Actors ── */}
      {actors.length > 0 && (
        <div className="ioc-detail-section">
          <h3 className="ioc-detail-section-title">
            Threat actors ({actors.length})
          </h3>
          <div className="actor-detail-chip-row">
            {actors.map((actor) => (
              <Link
                key={actor.actor_id}
                to={actorDetailPath(actor.common_name || actor.actor_id)}
                className="actor-detail-family-chip"
                title={`${actor.actor_id}${actor.doc_count ? ` — ${actor.doc_count} doc${actor.doc_count !== 1 ? 's' : ''}` : ''}`}
              >
                {actor.common_name || actor.actor_id}
                {actor.doc_count > 0 && (
                  <span style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.85em' }}>
                    {actor.doc_count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Families ── */}
      {families.length > 0 && (
        <div className="ioc-detail-section">
          <h3 className="ioc-detail-section-title">
            Top families ({families.length})
          </h3>
          <div className="actor-detail-chip-row">
            {families.map((fam) => (
              <Link
                key={fam.family_id || fam.common_name}
                to={familyDetailPath(fam.common_name || fam.family_id)}
                className="actor-detail-family-chip"
                title={`${fam.actor_count} actor${fam.actor_count !== 1 ? 's' : ''}`}
              >
                {fam.common_name || fam.family_id}
                {fam.actor_count > 0 && (
                  <span style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.85em' }}>
                    {fam.actor_count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Techniques ── */}
      {techniques.length > 0 && (
        <div className="ioc-detail-section">
          <h3 className="ioc-detail-section-title">
            ATT&amp;CK techniques ({techniques.length})
          </h3>
          <div className="actor-detail-chip-row">
            {techniques.map((tech) => (
              <Link
                key={tech.attack_id}
                to={techniqueDetailPath(tech.attack_id)}
                className="actor-detail-technique-chip"
              >
                <span className="actor-detail-technique-id">{tech.attack_id}</span>
                {tech.name && <span className="actor-detail-technique-name">{tech.name}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Documents ── */}
      {documents.length > 0 && (
        <div className="ioc-detail-section">
          <h3 className="ioc-detail-section-title">
            Recent documents ({documents.length})
          </h3>
          <table className="ioc-detail-doc-table">
            <thead>
              <tr>
                <th>Report</th>
                <th className="ioc-detail-col-org">Organization</th>
                <th className="ioc-detail-col-date">Date</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocs.map((doc) => (
                <tr key={doc.cite_key}>
                  <td>
                    <Link to={`/sources/${encodeURIComponent(doc.cite_key)}`} className="ioc-detail-doc-title">
                      {doc.title || doc.cite_key}
                    </Link>
                  </td>
                  <td className="ioc-detail-col-org">
                    <span className="ioc-detail-doc-org">{doc.organization || ''}</span>
                  </td>
                  <td className="ioc-detail-col-date">
                    <span className="ioc-detail-doc-date">{doc.date || ''}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {documents.length > PAGE_SIZE && !docsExpanded && (
            <button className="ioc-detail-show-more" onClick={() => setDocsExpanded(true)}>
              Show all {documents.length} documents
            </button>
          )}
        </div>
      )}
    </div>
  );
}
